import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Level, LevelFormData } from '@/types'
import {
  ArrowLeft,
  Save,
  Layers,
  Edit
} from 'lucide-react'

export default function LevelForm() {
  const { levelId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isEditing = Boolean(levelId) && levelId !== 'new'
  const isViewMode = isEditing && !location.pathname.endsWith('/edit')

  const [formData, setFormData] = useState<LevelFormData>({
    name: '',
    description: '',
    order_num: 0,
    is_active: true
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isEditing) {
      loadLevel()
    } else {
      loadNextOrderNum()
    }
  }, [levelId])

  const loadLevel = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('levels')
        .select('*')
        .eq('id', levelId as string)
        .single()

      if (error) throw error

      const levelData = data as unknown as Level

      setFormData({
        name: levelData.name,
        description: levelData.description || '',
        order_num: levelData.order_num,
        is_active: levelData.is_active
      })
    } catch (error) {
      console.error('Error loading level:', error)
      setError('Error al cargar el nivel')
    } finally {
      setIsLoading(false)
    }
  }

  const loadNextOrderNum = async () => {
    try {
      const { data } = await supabase
        .from('levels')
        .select('order_num')
        .order('order_num', { ascending: false })
        .limit(1)

      const orderData = data as unknown as { order_num: number }[]
      if (orderData && orderData.length > 0) {
        setFormData(prev => ({ ...prev, order_num: orderData[0].order_num + 1 }))
      }
    } catch (error) {
      console.error('Error loading order num:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSaving(true)

    try {
      if (isEditing) {
        const { error } = await (supabase
          .from('levels') as any)
          .update({
            name: formData.name,
            description: formData.description,
            order_num: formData.order_num,
            is_active: formData.is_active,
            updated_at: new Date().toISOString()
          })
          .eq('id', levelId as string)

        if (error) throw error
      } else {
        const { error } = await (supabase
          .from('levels') as any)
          .insert({
            name: formData.name,
            description: formData.description,
            order_num: formData.order_num,
            is_active: formData.is_active
          })

        if (error) throw error
      }

      navigate('/admin/levels')
    } catch (error: any) {
      console.error('Error saving level:', error)
      setError(error.message || 'Error al guardar el nivel')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/levels"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isViewMode ? 'Visualizar Nivel' : (isEditing ? 'Editar Nivel' : 'Nuevo Nivel')}
            </h1>
            <p className="text-gray-600">
              {isViewMode ? 'Vista previa del nivel' : (isEditing ? 'Modifica los datos del nivel' : 'Crea un nuevo nivel para organizar las unidades')}
            </p>
          </div>
        </div>
        {isViewMode && (
          <Link
            to={`/admin/levels/${levelId}/edit`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Edit className="w-5 h-5" />
            Editar
          </Link>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-indigo-100">
              <Layers className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Información del Nivel</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="level-order" className="block text-sm font-medium text-gray-700 mb-1">
                Orden
              </label>
              <input id="level-order" type="number"
                min="0"
                value={formData.order_num}
                onChange={(e) => setFormData(prev => ({ ...prev, order_num: Number.parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              />
            </div>
            <div className="md:col-span-3">
              <label htmlFor="level-name" className="block text-sm font-medium text-gray-700 mb-1">
                Nombre del Nivel
              </label>
              <input id="level-name" type="text" value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Comienzos del francés"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              />
            </div>
          </div>

          <div>
            <label htmlFor="level-description" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              id="level-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe el contenido y objetivos de este nivel..."
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              className="w-4 h-4 text-[#0055A4] border-gray-300 rounded focus:ring-[#0055A4] disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              Nivel activo (visible para seleccionar en cohortes)
            </label>
          </div>
        </div>

        {/* Actions */}
        {!isViewMode && (
          <div className="flex justify-end gap-3">
            <Link
              to="/admin/levels"
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Nivel')}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}
